import React, { useState, useEffect, useReducer, useCallback, useContext } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import { makeStyles, alpha } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import MainContainer from "../../components/MainContainer";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import ScheduleModal from "../../components/ScheduleModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import moment from "moment";
import { SocketContext } from "../../context/Socket/SocketContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Calendar, momentLocalizer } from "react-big-calendar";
import "moment/locale/pt-br";
import "react-big-calendar/lib/css/react-big-calendar.css";
import SearchIcon from "@material-ui/icons/Search";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import EventIcon from "@material-ui/icons/Event";
import AddIcon from "@material-ui/icons/Add";

import "./Schedules.css";
import { createMomentLocalizer } from "../../translate/calendar-locale";

function getUrlParam(paramName) {
  const searchParams = new URLSearchParams(window.location.search);
  return searchParams.get(paramName);
}

const eventTitleStyle = {
  fontSize: "14px",
  overflow: "hidden",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
};

var defaultMessages = {
  date: i18n.t("schedules.messages.date"),
  time: i18n.t("schedules.messages.time"),
  event: i18n.t("schedules.messages.event"),
  allDay: i18n.t("schedules.messages.allDay"),
  week: i18n.t("schedules.messages.week"),
  work_week: i18n.t("schedules.messages.work_week"),
  day: i18n.t("schedules.messages.day"),
  month: i18n.t("schedules.messages.month"),
  previous: i18n.t("schedules.messages.previous"),
  next: i18n.t("schedules.messages.next"),
  yesterday: i18n.t("schedules.messages.yesterday"),
  tomorrow: i18n.t("schedules.messages.tomorrow"),
  today: i18n.t("schedules.messages.today"),
  agenda: i18n.t("schedules.messages.agenda"),
  noEventsInRange: i18n.t("schedules.messages.noEventsInRange"),
  showMore: function showMore(total) {
    return "+" + total + " " + i18n.t("schedules.messages.showMore");
  }
};

const reducer = (state, action) => {
  if (action.type === "LOAD_SCHEDULES") {
    return [...state, ...action.payload];
  }

  if (action.type === "UPDATE_SCHEDULES") {
    const schedule = action.payload;
    const scheduleIndex = state.findIndex((s) => s.id === schedule.id);

    if (scheduleIndex !== -1) {
      state[scheduleIndex] = schedule;
      return [...state];
    } else {
      return [schedule, ...state];
    }
  }

  if (action.type === "DELETE_SCHEDULE") {
    const scheduleId = action.payload;
    return state.filter((s) => s.id !== scheduleId);
  }

  if (action.type === "RESET") {
    return [];
  }

  return state;
};

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    background: theme.palette.type === "dark"
      ? "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)"
      : "linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%)",
    minHeight: "100%",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    padding: theme.spacing(2, 3),
    background: theme.palette.type === "dark"
      ? alpha(theme.palette.background.paper, 0.6)
      : alpha("#fff", 0.8),
    backdropFilter: "blur(10px)",
    borderBottom: `1px solid ${theme.palette.type === "dark" ? alpha("#fff", 0.1) : alpha("#000", 0.08)}`,
    borderRadius: "16px 16px 0 0",
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
      padding: theme.spacing(2),
    },
  },
  titleWrapper: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  titleIcon: {
    width: 48,
    height: 48,
    borderRadius: 12,
    background: `linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: `0 6px 20px ${alpha("#9c27b0", 0.35)}`,
    "& svg": {
      color: "#fff",
      fontSize: 24,
    },
  },
  titleText: {
    fontWeight: 700,
    fontSize: "1.5rem",
    background: theme.palette.type === "dark"
      ? "linear-gradient(135deg, #fff 0%, #ccc 100%)"
      : "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
  },
  titleCount: {
    background: alpha(theme.palette.primary.main, 0.15),
    color: theme.palette.primary.main,
    padding: theme.spacing(0.5, 1.5),
    borderRadius: 20,
    fontSize: "0.85rem",
    fontWeight: 600,
    marginLeft: theme.spacing(1),
  },
  actionsWrapper: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  searchField: {
    minWidth: 280,
    "& .MuiOutlinedInput-root": {
      borderRadius: 12,
      background: theme.palette.type === "dark"
        ? alpha(theme.palette.background.default, 0.5)
        : "#fff",
      transition: "all 0.2s ease",
      "&:hover": {
        boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
      },
      "&.Mui-focused": {
        boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.3)}`,
      },
    },
    [theme.breakpoints.down("sm")]: {
      minWidth: "100%",
    },
  },
  addButton: {
    borderRadius: 12,
    padding: theme.spacing(1.2, 3),
    fontWeight: 600,
    textTransform: "none",
    fontSize: "0.95rem",
    background: `linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)`,
    boxShadow: `0 4px 14px ${alpha("#9c27b0", 0.35)}`,
    "&:hover": {
      background: `linear-gradient(135deg, #7b1fa2 0%, #6a1b9a 100%)`,
      boxShadow: `0 6px 20px ${alpha("#9c27b0", 0.45)}`,
      transform: "translateY(-1px)",
    },
  },
  mainPaper: {
    flex: 1,
    margin: theme.spacing(0, 2, 2, 2),
    padding: theme.spacing(2),
    borderRadius: 16,
    overflow: "hidden",
    background: theme.palette.type === "dark"
      ? alpha(theme.palette.background.paper, 0.8)
      : theme.palette.background.paper,
    backdropFilter: "blur(10px)",
    border: `1px solid ${theme.palette.type === "dark" ? alpha("#fff", 0.1) : alpha("#000", 0.05)}`,
    boxShadow: theme.palette.type === "dark"
      ? "0 8px 32px rgba(0, 0, 0, 0.3)"
      : "0 4px 20px rgba(0, 0, 0, 0.08)",
    overflowY: "auto",
    ...theme.scrollbarStyles,
    "& .rbc-calendar": {
      fontFamily: theme.typography.fontFamily,
    },
    "& .rbc-header": {
      padding: theme.spacing(1.5),
      fontWeight: 600,
      color: theme.palette.text.primary,
      borderBottom: `2px solid ${theme.palette.primary.main}`,
      background: theme.palette.type === "dark"
        ? alpha(theme.palette.background.default, 0.5)
        : alpha(theme.palette.grey[100], 0.8),
    },
    "& .rbc-month-view": {
      borderRadius: 12,
      border: `1px solid ${theme.palette.type === "dark" ? alpha("#fff", 0.1) : alpha("#000", 0.1)}`,
      overflow: "hidden",
    },
    "& .rbc-day-bg": {
      transition: "background 0.2s ease",
      "&:hover": {
        background: theme.palette.type === "dark"
          ? alpha(theme.palette.primary.main, 0.08)
          : alpha(theme.palette.primary.main, 0.04),
      },
    },
    "& .rbc-today": {
      background: alpha(theme.palette.primary.main, 0.1),
    },
    "& .rbc-event": {
      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
      borderRadius: 8,
      border: "none",
      boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}`,
      padding: theme.spacing(0.5, 1),
      "&:focus": {
        outline: `2px solid ${theme.palette.primary.light}`,
      },
    },
    "& .rbc-toolbar": {
      marginBottom: theme.spacing(2),
      flexWrap: "wrap",
      gap: theme.spacing(1),
    },
    "& .rbc-toolbar button": {
      borderRadius: 8,
      padding: theme.spacing(0.75, 1.5),
      fontWeight: 500,
      border: `1px solid ${theme.palette.type === "dark" ? alpha("#fff", 0.2) : alpha("#000", 0.2)}`,
      background: theme.palette.type === "dark"
        ? alpha(theme.palette.background.paper, 0.5)
        : "#fff",
      color: theme.palette.text.primary,
      transition: "all 0.2s ease",
      "&:hover": {
        background: alpha(theme.palette.primary.main, 0.1),
        borderColor: theme.palette.primary.main,
      },
      "&.rbc-active": {
        background: theme.palette.primary.main,
        color: "#fff",
        borderColor: theme.palette.primary.main,
        "&:hover": {
          background: theme.palette.primary.dark,
        },
      },
    },
    "& .rbc-off-range-bg": {
      background: theme.palette.type === "dark"
        ? alpha(theme.palette.background.default, 0.3)
        : alpha(theme.palette.grey[200], 0.5),
    },
    "& .rbc-date-cell": {
      padding: theme.spacing(0.5),
      "& > a": {
        color: theme.palette.text.primary,
        fontWeight: 500,
      },
    },
    "& .rbc-month-row": {
      borderColor: theme.palette.type === "dark" ? alpha("#fff", 0.1) : alpha("#000", 0.1),
    },
  },
}));

const Schedules = () => {
  const localizer = createMomentLocalizer();
  const classes = useStyles();
  const history = useHistory();

  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [deletingSchedule, setDeletingSchedule] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [schedules, dispatch] = useReducer(reducer, []);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [contactId, setContactId] = useState(+getUrlParam("contactId"));


  const fetchSchedules = useCallback(async () => {
    try {
      const { data } = await api.get("/schedules/", {
        params: { searchParam, pageNumber },
      });

      dispatch({ type: "LOAD_SCHEDULES", payload: data.schedules });
      setHasMore(data.hasMore);
      setLoading(false);
    } catch (err) {
      toastError(err);
    }
  }, [searchParam, pageNumber]);

  const handleOpenScheduleModalFromContactId = useCallback(() => {
    if (contactId) {
      handleOpenScheduleModal();
    }
  }, [contactId]);

  const socketManager = useContext(SocketContext);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchSchedules();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [
    searchParam,
    pageNumber,
    contactId,
    fetchSchedules,
    handleOpenScheduleModalFromContactId,
  ]);

  useEffect(() => {
    handleOpenScheduleModalFromContactId();
    const socket = socketManager.getSocket(user.companyId);

    socket.on(`company${user.companyId}-schedule`, (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_SCHEDULES", payload: data.schedule });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_SCHEDULE", payload: +data.scheduleId });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [handleOpenScheduleModalFromContactId, socketManager, user]);

  const cleanContact = () => {
    setContactId("");
  };

  const handleOpenScheduleModal = () => {
    setSelectedSchedule(null);
    setScheduleModalOpen(true);
  };

  const handleCloseScheduleModal = () => {
    setSelectedSchedule(null);
    setScheduleModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditSchedule = (schedule) => {
    setSelectedSchedule(schedule);
    setScheduleModalOpen(true);
  };

  const handleDeleteSchedule = async (scheduleId) => {
    try {
      await api.delete(`/schedules/${scheduleId}`);
      toast.success(i18n.t("schedules.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingSchedule(null);
    setSearchParam("");
    setPageNumber(1);

    dispatch({ type: "RESET" });
    setPageNumber(1);
    await fetchSchedules();
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const truncate = (str, len) => {
    if (str.length > len) {
      return str.substring(0, len) + "...";
    }
    return str;
  };

  return (
    <MainContainer className={classes.mainContainer}>
      <ConfirmationModal
        title={
          deletingSchedule &&
          `${i18n.t("schedules.confirmationModal.deleteTitle")}`
        }
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={() => handleDeleteSchedule(deletingSchedule.id)}
      >
        {i18n.t("schedules.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <ScheduleModal
        open={scheduleModalOpen}
        onClose={handleCloseScheduleModal}
        reload={fetchSchedules}
        aria-labelledby="form-dialog-title"
        scheduleId={selectedSchedule && selectedSchedule.id}
        contactId={contactId}
        cleanContact={cleanContact}
      />
      
      {/* Modern Header */}
      <Box className={classes.header}>
        <Box className={classes.titleWrapper}>
          <Box className={classes.titleIcon}>
            <EventIcon />
          </Box>
          <Typography className={classes.titleText}>
            {i18n.t("schedules.title")}
          </Typography>
          <span className={classes.titleCount}>{schedules.length}</span>
        </Box>
        <Box className={classes.actionsWrapper}>
          <TextField
            className={classes.searchField}
            placeholder={i18n.t("contacts.searchPlaceholder")}
            type="search"
            variant="outlined"
            size="small"
            value={searchParam}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: "gray" }} />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            color="primary"
            className={classes.addButton}
            onClick={handleOpenScheduleModal}
            startIcon={<AddIcon />}
          >
            {i18n.t("schedules.buttons.add")}
          </Button>
        </Box>
      </Box>
      
      <Paper className={classes.mainPaper} onScroll={handleScroll}>
        <Calendar
          messages={defaultMessages}
          formats={{
            agendaDateFormat: "DD/MM ddd",
            weekdayFormat: "dddd"
          }}
          localizer={localizer}
          events={schedules.map((schedule) => ({
            title: (
              <div className="event-container">
                <div style={eventTitleStyle}>{schedule.contact.name}</div>
                <DeleteOutlineIcon
                  onClick={() => handleDeleteSchedule(schedule.id)}
                  className="delete-icon"
                />
                <EditIcon
                  onClick={() => {
                    handleEditSchedule(schedule);
                    setScheduleModalOpen(true);
                  }}
                  className="edit-icon"
                />
              </div>
            ),
            start: new Date(schedule.sendAt),
            end: new Date(schedule.sendAt),
          }))}
          startAccessor="start"
          endAccessor="end"
          style={{ height: 600 }}
        />
      </Paper>
    </MainContainer>
  );
};

export default Schedules;
